/**
 * AdminEmail.jsx
 *
 * Two-way email console for AADC admins:
 *   - Compose tab: send a one-off email via /api/admin/email/send
 *   - Inbox tab:   view inbound + outbound messages from /api/admin/email/inbox
 *
 * Requires Resend Pro (already configured per RESEND_API_KEY +
 * RESEND_WEBHOOK_SECRET in backend .env).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { Send, Inbox, RefreshCw, ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminEmail() {
  const { user, loading: authLoading } = useAuth();
  const isAdmin = !!(user && (user.is_admin || user.is_sysadmin || user.role === 'admin'));
  if (authLoading) return null;
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-email-page">
      <SEO title="Admin Email" url="/admin/messages" noindex />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6">Admin Email</h1>
        <Tabs defaultValue="compose">
          <TabsList>
            <TabsTrigger value="compose" data-testid="email-tab-compose">
              <Send className="w-4 h-4 mr-2" /> Compose
            </TabsTrigger>
            <TabsTrigger value="inbox" data-testid="email-tab-inbox">
              <Inbox className="w-4 h-4 mr-2" /> Inbox
            </TabsTrigger>
          </TabsList>
          <TabsContent value="compose"><ComposeForm /></TabsContent>
          <TabsContent value="inbox"><Inboxer /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ComposeForm() {
  const [form, setForm] = useState({ to: '', subject: '', html: '', from_email: '' });
  const [sending, setSending] = useState(false);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const send = async () => {
    if (!form.to || !form.subject || !form.html) {
      toast.error('To, subject, and body are required');
      return;
    }
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/email/send`, {
        to: form.to,
        subject: form.subject,
        html: form.html,
        from_email: form.from_email || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Email sent');
      setForm({ to: '', subject: '', html: '', from_email: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="mt-4">
      <CardContent className="p-6 space-y-4">
        <div>
          <Label htmlFor="to">To</Label>
          <Input id="to" value={form.to} onChange={(e) => setF('to', e.target.value)} placeholder="recipient@example.com" data-testid="compose-to" />
        </div>
        <div>
          <Label htmlFor="from">From (optional)</Label>
          <Input id="from" value={form.from_email} onChange={(e) => setF('from_email', e.target.value)} placeholder="gbickford@verybigbox.com (defaults to system sender)" data-testid="compose-from" />
        </div>
        <div>
          <Label htmlFor="subject">Subject</Label>
          <Input id="subject" value={form.subject} onChange={(e) => setF('subject', e.target.value)} data-testid="compose-subject" />
        </div>
        <div>
          <Label htmlFor="html">Body (HTML allowed)</Label>
          <Textarea id="html" rows={12} value={form.html} onChange={(e) => setF('html', e.target.value)} placeholder="<p>Hello,</p>..." data-testid="compose-html" />
        </div>
        <div className="flex justify-end">
          <Button onClick={send} disabled={sending} data-testid="compose-send-btn">
            {sending ? 'Sending…' : (<><Send className="w-4 h-4 mr-2" /> Send</>)}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Inboxer() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');     // '' | 'inbound' | 'outbound'
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = filter ? `?direction=${filter}` : '';
      const r = await axios.get(`${API}/admin/email/inbox${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="mt-4 space-y-3" data-testid="email-inbox-list">
      <div className="flex items-center gap-2">
        <Button size="sm" variant={filter === '' ? 'default' : 'outline'} onClick={() => setFilter('')} data-testid="inbox-filter-all">All</Button>
        <Button size="sm" variant={filter === 'inbound' ? 'default' : 'outline'} onClick={() => setFilter('inbound')} data-testid="inbox-filter-inbound">Inbound</Button>
        <Button size="sm" variant={filter === 'outbound' ? 'default' : 'outline'} onClick={() => setFilter('outbound')} data-testid="inbox-filter-outbound">Outbound</Button>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={load} data-testid="inbox-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      {loading && <div className="text-sm text-stone-500">Loading…</div>}
      {!loading && items.length === 0 && <div className="text-sm text-stone-500 text-center py-8">No messages yet.</div>}
      {!loading && items.map((m) => (
        <Card key={m.id} data-testid={`email-${m.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="flex-shrink-0">
                {m.direction === 'inbound' ? <ArrowLeft className="w-3 h-3 mr-1" /> : <ArrowRight className="w-3 h-3 mr-1" />}
                {m.direction}
              </Badge>
              <div className="min-w-0 flex-1">
                <div className="font-medium">{m.subject || <span className="text-stone-400">(no subject)</span>}</div>
                <div className="text-xs text-stone-500 mt-0.5">
                  {m.direction === 'inbound' ? `From: ${m.from_email}` : `To: ${m.to}`}
                  {' · '}{new Date(m.received_at).toLocaleString()}
                </div>
                {m.body_text && <p className="text-sm text-stone-700 mt-2 whitespace-pre-wrap line-clamp-3">{m.body_text}</p>}
                {!m.body_text && m.body_html && <p className="text-sm text-stone-500 mt-2 italic">[HTML body — open in detail view]</p>}
                {m.status && <Badge variant="outline" className="text-xs mt-2">{m.status}</Badge>}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
